/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  SortDirectionEnum,
  useAllWorkspaces,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  BaseColumnCheckbox,
  Colors,
  ControlledPhoneInput,
  ControlledSelect,
  ControlledTextInput,
  InfiniteDataGrid,
  PhoneValidator,
  Select,
  Spacing,
  tokens,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { array, string, object as yupObject } from 'yup';
import { FormComponent } from '../../shared/components/forms/mutationForm';

const FIND_WORKSPACE_ROUTINES = `
  query FindWorkspaceRoutines($orderBy: OrderByInput, $filter: WorkspaceRoutineFilter, $page: PageRequest) {
    findWorkspaceRoutines(orderBy: $orderBy, filter: $filter, page: $page) {
      items {
        id
        name
      }
      count
      limit
      offset
    }
  }
`;

const ROUTINE_GRID_HEIGHT = 300;

export const CreateWorkspaceForm: FormComponent = (props) => {
  const DEFAULT_ROUTINE_WORKSPACE_ID = '00000000-0000-0000-0000-000000000001';
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [routineFilter, setRoutineFilter] = useState<any>({
    workspace: { eq: DEFAULT_ROUTINE_WORKSPACE_ID },
    deactivated: { ne: true },
  });
  // When true, isRowSelected returns true for all rows so new data loads
  // with every routine pre-selected. Flipped to false after the first
  // updateRowsSelected callback fires.
  const selectAllOnLoad = useRef(true);

  const data = useAllWorkspaces();
  const { t } = useTranslation();

  const formSchema = yupObject({
    name: string().required(t('workspace.create.validation.name_required')),
    type: string()
      .oneOf(Object.values(WorkspaceType))
      .required('Workspace type required'),
    ownerFirstName: string().required(
      t('workspace.create.validation.admin_first_name_required')
    ),
    ownerLastName: string().required(
      t('workspace.create.validation.admin_last_name_required')
    ),
    ownerEmail: string().email().optional().nullable().lowercase(),
    ownerPhone: PhoneValidator.optional().nullable(),
    routineIds: array().of(string()).optional().default([]),
  }).test(
    'email or phone',
    (value) => !!(value.ownerPhone || value.ownerEmail)
  );

  const { control, handleSubmit, setValue, formState } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      ...props.defaultValues,
      ownerPhone: null,
      type: WorkspaceType.Producer,
    },
  });

  const selectedRoutineIds: string[] =
    useWatch({ control, name: 'routineIds' }) ?? [];

  useEffect(() => {
    if (!data) return;
    setWorkspaces(data.map((w) => ({ label: w.name, value: w.id })));
  }, [data]);

  const onRoutineWorkspaceChanged = useCallback((item: any) => {
    selectAllOnLoad.current = true;
    setRoutineFilter({
      workspace: { eq: item },
      deactivated: { ne: true },
    });
  }, []);

  const handleRowsSelected = useCallback(
    (rows: any[]) => {
      const ids = rows.map((r: any) => r.id);
      setValue('routineIds', ids, { shouldValidate: true });
      selectAllOnLoad.current = false;
    },
    [setValue]
  );

  const isRowSelected = useCallback(
    (row: any) => {
      if (selectAllOnLoad.current) return true;
      return selectedRoutineIds.includes(row.id);
    },
    [selectedRoutineIds]
  );

  // --- columns ---
  const columnHelper = createColumnHelper<any>();
  const columns = [
    columnHelper.accessor('selected', {
      cell: BaseColumnCheckbox.checkbox,
      header: BaseColumnCheckbox.checkboxHeader,
      meta: { style: styles.checkboxCell },
    }),
    columnHelper.accessor('name', {
      cell: (info: any) => (
        <Text style={styles.routineName}>{info.getValue()}</Text>
      ),
      header: () =>
        t('workspace.create.routineName', { defaultValue: 'Routine' }),
      meta: { style: styles.nameCell },
    }),
  ] as ColumnDef<any, any>[];

  return (
    <View style={styles.container}>
      <ScrollView
        scrollEnabled
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.formItem}>
          <View>
            <Text style={styles.fieldLabel}>
              {t('workspace.create.workspaceName')}
            </Text>
          </View>
          <ControlledTextInput
            control={control}
            name="name"
            autoFocus={true}
            placeholder={t('workspace.create.workspaceName')}
            useCropCircle
            backgroundColor={Colors.neutral[200]}
            hideBorder
            outlinedTextInput
            disableInnerStyle
          />
        </View>

        <View style={[styles.formItem, { zIndex: 1000 }]}>
          <View>
            <Text style={styles.fieldLabel}>{t('workspace.create.type')}</Text>
          </View>
          <ControlledSelect
            control={control}
            name="type"
            label={''}
            items={Object.entries(WorkspaceType).map(([, value]) => ({
              label: t(`workspace.types.${value}`),
              value: value,
            }))}
          />
        </View>

        <View style={styles.formItem}>
          <View>
            <Text style={styles.fieldLabel}>
              {t('workspace.create.administratorFirstName')}
            </Text>
          </View>
          <ControlledTextInput
            control={control}
            name="ownerFirstName"
            useCropCircle
            backgroundColor={Colors.neutral[200]}
            hideBorder
            outlinedTextInput
            disableInnerStyle
          />
        </View>

        <View style={styles.formItem}>
          <View>
            <Text style={styles.fieldLabel}>
              {t('workspace.create.administratorLastName')}
            </Text>
          </View>
          <ControlledTextInput
            control={control}
            name="ownerLastName"
            useCropCircle
            backgroundColor={Colors.neutral[200]}
            hideBorder
            outlinedTextInput
            disableInnerStyle
          />
        </View>

        <View style={styles.formItem}>
          <View>
            <Text style={styles.fieldLabel}>
              {t('workspace.create.administratorEmail')}
            </Text>
          </View>
          <ControlledTextInput
            control={control}
            name="ownerEmail"
            isLowerCase
            useCropCircle
            backgroundColor={Colors.neutral[200]}
            hideBorder
            outlinedTextInput
            disableInnerStyle
          />
        </View>

        <View style={styles.formItem}>
          <View>
            <Text style={styles.fieldLabel}>
              {t('workspace.create.administratorPhone')}
            </Text>
          </View>
          <ControlledPhoneInput
            control={control}
            name="ownerPhone"
            placeholder={'phone'}
            backgroundColor={Colors.neutral[200]}
          />
        </View>

        <View style={[styles.formItem, { zIndex: 999 }]}>
          <View>
            <Text style={styles.fieldLabel}>
              {t('workspace.create.copyRoutines')}
            </Text>
          </View>
          <Select
            label={t('routines.copyToWorkspace.workspace')}
            items={workspaces}
            dropDownPickerProps={{
              value: DEFAULT_ROUTINE_WORKSPACE_ID,
            }}
            onSelectItem={onRoutineWorkspaceChanged}
          />
        </View>

        <View style={styles.gridSection}>
          <InfiniteDataGrid
            modelName="WorkspaceRoutine"
            query={FIND_WORKSPACE_ROUTINES}
            filter={routineFilter}
            orderBy={{ field: 'order', order: SortDirectionEnum.Asc }}
            pageSize={500}
            columns={columns}
            hideHeader={false}
            hideResultsLabel={true}
            listMaxHeight={ROUTINE_GRID_HEIGHT}
            containerStyle={{ height: ROUTINE_GRID_HEIGHT }}
            paddingBottom={0}
            textEmpty={t('workspace.create.noRoutines', {
              defaultValue: 'No routines found',
            })}
            updateRowsSelected={handleRowsSelected}
            isRowSelected={isRowSelected}
            showScrollIndicator={true}
          />
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          style={[
            styles.footerButton,
            !formState.isValid && styles.footerButtonDisabled,
          ]}
          onPress={formState.isValid ? handleSubmit(props.onSubmit) : undefined}
          disabled={!formState.isValid}
        >
          <Text style={styles.footerButtonText}>
            {t('workspace.create.addButton')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: tokens.colors.brand.white,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingBottom: 120,
  },
  formItem: {
    paddingVertical: tokens.spacing.md,
    flexDirection: 'column',
    width: '100%',
  },
  fieldLabel: {
    ...tokens.typography.body.small,
    fontWeight: '600',
    marginBottom: 8,
  },
  gridSection: {
    paddingVertical: tokens.spacing.md,
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameCell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: 4,
    ...Platform.select({
      web: { height: 50, paddingRight: 16 },
      default: { height: 68, paddingRight: 12 },
    }),
  },
  routineName: {
    ...tokens.typography.body.small,
    color: tokens.colors.neutral[800],
  },
  footer: {
    position: 'absolute',
    left: tokens.spacing.xl,
    right: tokens.spacing.xl,
    bottom: 0,
    paddingVertical: tokens.spacing.xl,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  footerButton: {
    backgroundColor: tokens.colors.neutral[800],
    borderRadius: tokens.borderRadius.sm,
    width: '100%',
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerButtonDisabled: {
    backgroundColor: tokens.colors.neutral[400],
  },
  footerButtonText: {
    color: tokens.colors.brand.white,
    fontWeight: tokens.typography.body.mediumEmphasised.fontWeight,
    fontSize: 16,
  },
});
